/**
 * UpdatePrompt — surfaces "a new version is available, reload?" when the
 * Vite-PWA service worker has installed a new precache in the background.
 *
 * Without this, deploys appear to "not land" for any user with the PWA
 * already installed: the SW intercepts navigations and serves the cached
 * old `index.html` → old bundle hash → old UI. The new SW activates
 * silently but the running page has no way to know.
 *
 * `useRegisterSW` fires `needRefresh = true` when a new SW has finished
 * installing. Clicking the toast calls `updateServiceWorker(true)` which
 * reloads the page so the new bundle takes effect.
 *
 * Renders as a small toast at the bottom-center of the viewport, above
 * the SASS tab bar. Self-dismissable, but doesn't persist dismissal —
 * if the user later reloads on their own, the toast will reappear if
 * still applicable (it won't, because the reload pulls the new SW).
 */

import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect } from 'react';

export function UpdatePrompt(): JSX.Element | null {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(err) {
      // eslint-disable-next-line no-console
      console.warn('PWA SW register error', err);
    },
  });

  // Tell the SW to check for an update on every focus — covers the case
  // where the user keeps a long-lived tab open across multiple deploys.
  useEffect(() => {
    const onFocus = (): void => {
      void (async () => {
        try {
          const reg = await navigator.serviceWorker?.getRegistration();
          if (reg !== undefined && reg !== null) await reg.update();
        } catch { /* swallow — best-effort */ }
      })();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!needRefresh) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(var(--sass-tab-bar-h, 56px) + 16px)',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: 'var(--bg, #0a0d0a)',
        border: '1px solid var(--phos, #78dc8c)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.45)',
        borderRadius: 6,
        padding: '10px 14px',
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        fontSize: 12,
        color: 'var(--ink, #d4e4dc)',
        maxWidth: 'min(440px, calc(100vw - 32px))',
      }}
    >
      <span style={{ color: 'var(--phos, #78dc8c)' }}>●</span>
      <span style={{ flex: 1 }}>A new version of ALDS is ready.</span>
      <button
        className="cta"
        style={{ fontSize: 11, padding: '4px 10px' }}
        onClick={() => { void updateServiceWorker(true); }}
      >
        ↻ reload
      </button>
      <button
        aria-label="Dismiss update notice"
        style={{
          background: 'transparent',
          border: 'none',
          color: 'var(--ink-dim, #a3b5ac)',
          cursor: 'pointer',
          fontSize: 14,
          padding: '2px 6px',
        }}
        onClick={() => setNeedRefresh(false)}
      >
        ✕
      </button>
    </div>
  );
}
